import React from "react";
import moment from "moment";
import TitleStripe from "../../common/TitleStripe";
import NoOcurrence from "../../common/query/NoOcurrence";
import Enderecos from "../../common/query/Enderecos";
import Emails from "../../common/query/Emails";
import Telefones from "../../common/query/Telefones";
import Sociedade from "../../common/query/Sociedade";
import {
  countDadosCadastrais,
  formatDocument,
  formatToReal,
} from "../../../utils/utils";
import { FaRegChartBar, FaUserCheck } from "react-icons/fa";
import { IoReceiptOutline } from "react-icons/io5";
import { LuBuilding2, LuMapPin } from "react-icons/lu";
import {
  GrDocumentExcel,
  GrDocumentText,
  GrDocumentTime,
} from "react-icons/gr";
import { CiMoneyCheck1 } from "react-icons/ci";
import { MdOutlineBalance, MdOutlineEmail } from "react-icons/md";
import {
  HiOutlineCurrencyDollar,
  HiOutlineLocationMarker,
} from "react-icons/hi";
import { FiPhone, FiUserCheck, FiUsers } from "react-icons/fi";

const buildDurationLabel = (fromMoment, suffix = "") => {
  if (!fromMoment || !fromMoment.isValid()) return null;
  const years = moment().diff(fromMoment, "years");
  const afterYears = fromMoment.clone().add(years, "years");
  const months = moment().diff(afterYears, "months");
  const parts = [];
  if (years) parts.push(`${years} ano${years === 1 ? "" : "s"}`);
  if (months) parts.push(`${months} mês${months === 1 ? "" : "es"}`);
  if (!parts.length) return `menos de 1 mês${suffix ? ` ${suffix}` : ""}`;
  const joined = parts.join(" e ");
  return suffix ? `${joined} ${suffix}` : joined;
};

const CadastralPJQuery = ({ data, dataFull }) => {
  const totalDadosCadastrais = Array.isArray(dataFull)
    ? dataFull.reduce((acc, item) => acc + countDadosCadastrais(item), 0)
    : countDadosCadastrais(dataFull);

  const podeRenderizarDadosCadastrais = totalDadosCadastrais < 3;
  const cadastro = data?.dadosCadastrais || {};
  const foundationMoment = cadastro.dataFundacao
    ? moment(
        cadastro.dataFundacao,
        ["DD/MM/YYYY", "YYYY-MM-DD", "YYYYMMDD"],
        true
      )
    : null;
  const foundationLabel =
    foundationMoment && foundationMoment.isValid()
      ? foundationMoment.format("DD/MM/YYYY")
      : null;
  const foundationAgeLabel = buildDurationLabel(
    foundationMoment,
    "de atividade"
  );
  const situacaoLabel = cadastro.situacao || "";
  const summaryCards = [
    {
      label: "CNPJ / MF",
      value: formatDocument(cadastro.cnpj) || "Não disponível",
      hint: "Documento",
    },
    {
      label: "CNAE principal",
      value: cadastro.ramoAtividade || "Não informado",
      hint: "Atividade",
    },
    {
      label: "Natureza jurídica",
      value: cadastro.naturezaJuridica || cadastro.natureza || "Não informado",
      hint: "Perfil",
    },
  ];

  return (
    <>
      {podeRenderizarDadosCadastrais && (
        <>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <LuBuilding2 className="text-blue-700" />{" "}
              <span>Dados Cadastrais</span>
            </div>
          </TitleStripe>
          <div className="text-xs">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                  Razão Social
                </p>
                <p className="text-xl font-semibold text-gray-900">
                  {cadastro.razaoSocial || "Indisponível"}
                </p>
                <p className="text-[11px] text-gray-500">
                  {cadastro.nomeFantasia || "Sem nome fantasia"}
                </p>
              </div>
            </div>

            <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
              <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                Situação: {situacaoLabel || "Desconhecida"}
              </span>
              <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                Fundação: {foundationLabel || "—"}
              </span>
              {foundationAgeLabel && (
                <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                  {foundationAgeLabel}
                </span>
              )}
            </div>

            <div className="mt-3 grid gap-2 grid-cols-3">
              {summaryCards.map((item) => (
                <div
                  key={item.label}
                  className="rounded-xl border border-gray-200 bg-slate-50 px-3 py-2"
                >
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                    {item.label}
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {item.value}
                  </p>
                  <p className="text-[10px] text-gray-500">{item.hint}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {data?.socios && (
        <div>
          <TitleStripe className="mb-5">
            <div className="flex items-center gap-2">
              <FiUsers className="text-blue-700" />
              <span>Quadro Societário</span>
            </div>
          </TitleStripe>
          {data?.socios?.length > 0 ? (
            <Sociedade socios={data?.socios} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.enderecos && (
        <div>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <HiOutlineLocationMarker className="text-blue-700" />{" "}
              <span>Endereços</span>
            </div>
          </TitleStripe>
          {data?.enderecos?.length > 0 ? (
            <Enderecos enderecos={data?.enderecos} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.emails && (
        <div>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <MdOutlineEmail className="text-blue-700" /> <span>E-mails</span>
            </div>
          </TitleStripe>
          {data?.emails?.length > 0 ? (
            <Emails emails={data?.emails} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.telefones && (
        <div>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <FiPhone className="text-blue-700" /> <span>Telefones</span>
            </div>
          </TitleStripe>
          {data?.telefones?.length > 0 ? (
            <Telefones telefones={data?.telefones} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}
    </>
  );
};

export default CadastralPJQuery;
